import React, { useCallback, useState } from 'react';
import DrawWithTwoHandApp from '../../../draw-with-two-hand/App';
import { useTranslation } from '../../../../shared/i18n';
import { completeSession, saveProgress } from '../../api/activitySessions';

/**
 * DrawWithTwoHandSession — wires the standalone DrawWithTwoHand React app to
 * the Rails ActivitySession. Counter is persisted via saveProgress after every
 * completed pair; the session is marked complete once the kid has finished
 * `required_pairs` pairs (X, default 10).
 */
export const DrawWithTwoHandSession = ({ session, activity, onComplete }) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requiredPairs = activity.required_pairs || 10;
  // Resume from the saved final_snapshot. Kid leaves and comes back at the
  // same pair index.
  const initialCounter = session.final_snapshot?.completed_pairs || 0;

  const handleProgressSave = useCallback(async (progressData) => {
    try {
      await saveProgress(session.id, progressData);
    } catch (error) {
      // Progress saves are best-effort — don't surface to the kid.
      console.error('Error saving Draw With Two Hand progress:', error);
    }
  }, [session.id]);

  const handleActivityComplete = useCallback(async (finalSnapshot) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await completeSession(session.id, finalSnapshot);
      if (result.status === 'success') {
        onComplete(result);
      } else {
        console.error('Unexpected completion status:', result.status);
        alert(result.message || 'Failed to complete activity');
      }
    } catch (error) {
      console.error('Error completing Draw With Two Hand session:', error);
      alert(error.message || 'Failed to complete activity');
    } finally {
      setIsSubmitting(false);
    }
  }, [session.id, onComplete, isSubmitting]);

  // The activity title is already shown in the ActivitySession header. The
  // labels below feed the inner DrawWithTwoHand React app (status text,
  // Tiếp tục button, pair counter). We use defaultValue fallbacks so the
  // session keeps rendering even if a translation key is missing.
  const labels = {
    title: activity.name,
    continue: t('children.activity_sessions.draw_with_two_hand_continue_button', {
      defaultValue: 'Tiếp tục',
    }),
    pairCounter: (counter, total) =>
      t('children.activity_sessions.draw_with_two_hand_pair_counter', {
        counter,
        total,
        defaultValue: `${counter}/${total} cặp`,
      }),
  };

  return (
    <DrawWithTwoHandApp
      activityId={activity.id}
      sessionId={session.id}
      requiredPairs={requiredPairs}
      initialCounter={initialCounter}
      onProgressSave={handleProgressSave}
      onComplete={handleActivityComplete}
      railsEnv={typeof window !== 'undefined' ? window.railsEnv : undefined}
      labels={labels}
    />
  );
};
